import { Users, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/Avatar'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Field, Select } from '@/components/ui/Field'
import { Tag } from '@/components/ui/Tag'
import { useSession } from '@/features/auth/api/useSession'
import { useColleagues } from '@/features/directories/api/useColleagues'
import { useRoleLabel } from '@/features/settings/api/useRoleNames'
import type { QuoteDraft } from '@/features/quotes/quoteDraft'

/* The INTERNAL team on step 1 of the builder (FR-14, migration 0028).
 *
 * ── What this is NOT ─────────────────────────────────────────────────────────
 *
 * It is not a list of EXTERNAL parties on the shipment — a shipper, a
 * consignee, a driver — who have no login. A quote names none since S-22a
 * dropped its participants (migration 0075). This card records COLLEAGUES, who
 * are picked from the staff roster because they already exist as accounts.
 *
 * While both lists existed the migrations forbade merging them in as many
 * words: "a login on a truck driver is the collapse 0017 forbids". A counterparty
 * is never a team member for the same reason.
 *
 * ── Why the rows are ids rather than drafts ──────────────────────────────────
 *
 * A typed party would be a draft with a local key and text fields to
 * validate. A colleague is CHOSEN, so the row is a user id and there
 * is nothing to validate: the name and the role come from the account, and the
 * picker only ever offers accounts the server would accept.
 *
 * ── Why there is no owner row here ───────────────────────────────────────────
 *
 * The owner is added to the team by the server, inside the create transaction.
 * Rendering them here would mean the client guessing at its own user id and
 * then sending it — and if the two ever disagreed, the card would show a team
 * the quote does not have. So the card shows the owner as a fixed first row
 * read from the SESSION, marked as the owner and with no remove control,
 * separate from the list the form actually submits.
 */
export function QuoteTeamCard({
  draft,
  setDraft,
}: {
  draft: QuoteDraft
  setDraft: (draft: QuoteDraft) => void
}) {
  const { t } = useTranslation()
  const { data: colleagues } = useColleagues()
  const { data: session } = useSession()
  // A custom role by the name its creator typed, never the key (story B3).
  const roleName = useRoleLabel()

  const roster = colleagues ?? []
  const chosen = draft.team

  /* Everybody who could still be added: on the roster, not already chosen, and
     not the owner — who is on the team by construction and whose row below
     carries no remove control. Offering them would produce a duplicate the
     server collapses silently, which reads to the user as a click that did
     nothing. */
  const available = roster.filter(
    (person) => !chosen.includes(person.id) && person.id !== session?.id,
  )

  const add = (userId: string) =>
    setDraft({ ...draft, team: [...chosen, userId] })

  const remove = (userId: string) =>
    setDraft({ ...draft, team: chosen.filter((id) => id !== userId) })

  /* Resolved against the roster rather than stored on the draft. The draft holds
     ids because that is what the payload sends; a name cached beside it would be
     a second copy that goes stale the day somebody is renamed. */
  const chosenPeople = chosen
    .map((id) => roster.find((person) => person.id === id))
    .filter((person) => person !== undefined)

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <Users size={15} className="text-text-muted" aria-hidden />
            {t('quotes.team.title')}
          </span>
        </CardTitle>
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('quotes.team.hint')}
        </p>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 pt-2">
        <ul className="flex flex-col gap-2">
          {/* The owner, always first and never removable. Read from the session
              rather than from the draft: the server puts them on the team, so
              this row states a fact rather than submitting one. */}
          {session ? (
            <li className="border-line-hair flex items-center gap-3 border-b pb-2">
              <Avatar name={session.name} size="md" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-text-heading text-[13px] leading-tight font-medium">
                  {session.name}
                  <span className="text-text-muted font-light">
                    {' · '}
                    {t('quotes.team.you')}
                  </span>
                </span>
                <span className="text-text-muted text-[12px] leading-snug font-light">
                  {roleName(session.role)}
                </span>
              </span>
              <Tag>{t('quotes.team.owner')}</Tag>
            </li>
          ) : null}

          {chosenPeople.map((person) => (
            <li key={person.id} className="flex items-center gap-3">
              <Avatar name={person.name} size="md" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-text-heading text-[13px] leading-tight font-medium">
                  {person.name}
                </span>
                <span className="text-text-muted text-[12px] leading-snug font-light">
                  {roleName(person.role)}
                  {' · '}
                  {person.office}
                </span>
              </span>
              {/* One press, like the participants card above and for its reason:
                  nothing is persisted yet, so this undoes a choice rather than
                  deleting a record. Arming a confirmation for "I picked the
                  wrong person" is friction with no risk behind it. */}
              <button
                type="button"
                onClick={() => remove(person.id)}
                aria-label={t('quotes.team.remove', { name: person.name })}
                title={t('quotes.team.remove', { name: person.name })}
                className="text-text-muted hover:text-negative hover:bg-surface-hover cursor-pointer rounded-[5px] p-1"
              >
                <X size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>

        {/* The picker resets to blank on every choice, so it reads as "add
            somebody" rather than as a field holding the last person added —
            the list above is what holds the answer. */}
        {available.length > 0 ? (
          <Field label={t('quotes.team.add')}>
            {(props) => (
              <Select
                value=""
                onChange={add}
                options={available.map((person) => ({
                  value: person.id,
                  label: person.name,
                  // Role and office as the second line: two colleagues sharing a
                  // first name is the ordinary case across two offices, and a
                  // list of bare names cannot be chosen from.
                  hint: `${roleName(person.role)} · ${person.office}`,
                }))}
                placeholder={t('quotes.team.add')}
                // Past about ten options a plain list is scrolled rather than
                // read; the roster is the whole company.
                searchable={roster.length > 10}
                {...props}
              />
            )}
          </Field>
        ) : (
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {chosen.length > 0
              ? t('quotes.team.noneAvailable')
              : t('quotes.team.empty')}
          </p>
        )}
      </CardBody>
    </Card>
  )
}
